import transportOffersController from "@/services/transportOffersService";
import parameterController from "@/services/parameterService";
import corporateController from "@/services/corporateService";
import documentChecklistController from "@/services/legalDocumentService";

import Swal from "sweetalert2";
import Multiselect from "@vueform/multiselect";
import "@vueform/multiselect/themes/default.css";

export default {
  name: "TransportOffersForm",

  components: { Multiselect },

  props: {
    mode: {
      type: String,
      required: true,
      validator: (v) => ["add", "edit", "detail"].includes(v),
    },
    id: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      // Master data
      corporates: [],
      contractTypes: [],

      // Form (add/edit)
      form: {
        mst_corporate_id: null,
        contract_type: null,
        has_pks: 0,
        pks_reason: "",
        status: 1,
        start_date: "",
        end_date: "",
        file: [],
        files: [],
      },
      removedFileIds: [],
      detail: {},
      checklist: this.getEmptyChecklist(),
      checklistPendingFiles: {},
      checklistDragging: null,

      isLoading: false,
      isFormReady: false,

      isDragging: false,
    };
  },

  computed: {
    isEditOrDetail() {
      return this.mode === "edit" || this.mode === "detail";
    },

    pageTitle() {
      return {
        add: "Add Dokumen",
        edit: "Edit Dokumen",
        detail: "Detail Dokumen",
      }[this.mode];
    },

    submitLabel() {
      return this.mode === "add" ? "Save" : "Update";
    },

    selectedContractType() {
      return this.contractTypes.find(
        (c) => c.value === this.form.contract_type,
      );
    },

    isFixedTerm() {
      const desc = this.selectedContractType?.description || "";
      return /fixed[\s-]?term/i.test(desc);
    },

    checklistLocalStatus() {
      if (!this.checklist.items?.length) {
        return {
          is_complete: this.checklist.is_complete,
          missing: this.checklist.missing,
        };
      }
      const missing = this.checklist.items.filter(
        (i) => i.required && !i.file_path && !this.checklistPendingFiles[i.key],
      );
      return {
        is_complete: missing.length === 0,
        missing: missing.map((i) => i.label),
      };
    },
  },

  watch: {
    isFixedTerm(val) {
      if (!val) {
        this.form.start_date = "";
        this.form.end_date = "";
      }
    },

    "form.mst_corporate_id"(newVal) {
      if (this.mode === "detail") return;
      if (!newVal) {
        this.checklist = this.getEmptyChecklist();
        this.checklistPendingFiles = {};
        return;
      }
      this.loadChecklistTemplate(newVal);
    },

    "form.has_pks"(newVal) {
      if (newVal == 1) {
        this.form.pks_reason = "";
      }
    },
  },

  methods: {
    /** Kembali ke halaman asal (Report H-60 / list penawaran) */
    goBack() {
      const from = this.$route.query.from;
      if (from && typeof from === "string" && from.startsWith("/")) {
        this.$router.push(from);
        return;
      }
      if (window.history.length > 1) {
        this.$router.back();
        return;
      }
      this.$router.push("/transport/transport-offers");
    },

    /** ISO / Date → yyyy-mm-dd untuk input type="date" (hindari geser timezone) */
    toDateInput(value) {
      if (!value) return "";
      const s = String(value);
      const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${mo}-${day}`;
    },

    /** Tampil tanggal (ambil bagian tanggal saja, tanpa shift TZ) */
    formatDate(value) {
      if (!value) return "-";
      const s = String(value);
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return d.toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return s.slice(0, 10);
      return d.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    // ───────── HELPERS ─────────
    getEmptyChecklist() {
      return { company_type: "PT", items: [], is_complete: false, missing: [] };
    },

    // ───────── MASTER DATA ─────────
    async loadMasterData() {
      const results = await Promise.allSettled([
        corporateController.getAll({ limit: 0 }),
        parameterController.getAll({ param_id: 4, limit: 0 }),
      ]);

      const [corpo, contract] = results;

      if (corpo.status === "fulfilled") {
        this.corporates = [
          ...new Map(
            corpo.value.data.map((item) => [item.mst_corporate_id, item]),
          ).values(),
        ];
      } else {
        console.warn("Corporate gagal dimuat:", corpo.reason);
      }

      if (contract.status === "fulfilled")
        this.contractTypes = contract.value.data || [];
    },

    // ───────── LOAD DETAIL / EDIT ─────────
    async loadDetail() {
      try {
        const res = await transportOffersController.getById(this.id);
        if (!res) return;

        if (this.mode === "detail") {
          this.detail = res;
          if (res.mst_corporate_id) {
            await this.loadChecklistTemplate(res.mst_corporate_id);
          }
        } else {
          Object.assign(this.form, {
            mst_corporate_id: res.mst_corporate_id,
            contract_type: res.contract_type,
            has_pks: res.has_pks ?? 0,
            pks_reason: res.pks_reason || "",
            status: res.status,
            start_date: this.toDateInput(res.start_date),
            end_date: this.toDateInput(res.end_date),
            file: [],
            files: res.files || [],
          });
          this.removedFileIds = [];
          if (res.mst_corporate_id) {
            await this.loadChecklistTemplate(res.mst_corporate_id);
          }
        }
      } catch (error) {
        console.error(error);
        Swal.fire(
          "Error",
          "Gagal memuat data penawaran harga biaya angkut",
          "error",
        );
      }
    },

    // ───────── CHECKLIST DOKUMEN LEGAL (per Corporate) ─────────
    async loadChecklistTemplate(corporateId) {
      const cid = corporateId || this.form.mst_corporate_id;
      if (!cid) return;
      try {
        this.checklist =
          await documentChecklistController.getChecklistByCorporate(cid);
      } catch (error) {
        console.error(error);
        this.checklist = this.getEmptyChecklist();
      }
    },

    handleChecklistUpload(key, event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file) this.stageChecklistFile(key, file);
    },

    handleChecklistDrop(key, event) {
      this.checklistDragging = null;
      const file = event.dataTransfer.files?.[0];
      if (file) this.stageChecklistFile(key, file);
    },

    stageChecklistFile(key, file) {
      const name = (file.name || "").toLowerCase();
      const type = file.type || "";
      const isAllowed =
        type === "application/pdf" ||
        type === "image/jpeg" ||
        type === "image/jpg" ||
        type === "image/png" ||
        /\.(pdf|jpe?g|png)$/.test(name);
      if (!isAllowed) {
        Swal.fire(
          "Warning",
          "Dokumen legalitas harus berformat PDF, JPG, JPEG, atau PNG",
          "warning",
        );
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        Swal.fire("Warning", "Ukuran file maksimal 10MB", "warning");
        return;
      }
      this.checklistPendingFiles = {
        ...this.checklistPendingFiles,
        [key]: file,
      };
    },
    fileIconClass(fileName) {
      const name = String(fileName || "")
        .split("|")[0]
        .toLowerCase();
      if (/\.(jpe?g|png)$/.test(name))
        return "bi-file-earmark-image-fill text-primary";
      if (/\.(docx?)$/.test(name))
        return "bi-file-earmark-word-fill text-primary";
      return "bi-file-earmark-pdf-fill text-danger";
    },

    cancelPendingChecklistFile(key) {
      const updated = { ...this.checklistPendingFiles };
      delete updated[key];
      this.checklistPendingFiles = updated;
    },

    triggerChecklistInput(key) {
      const ref = this.$refs["checklistInput_" + key];
      const el = Array.isArray(ref) ? ref[0] : ref;
      el?.click();
    },

    async uploadPendingChecklistFiles() {
      const entries = Object.entries(this.checklistPendingFiles);
      if (!entries.length) return;
      if (!this.form.mst_corporate_id) {
        throw new Error("Pilih Customer terlebih dahulu");
      }

      for (const [key, file] of entries) {
        await documentChecklistController.uploadFileByCorporate(
          this.form.mst_corporate_id,
          key,
          file,
        );
      }

      this.checklistPendingFiles = {};
      await this.loadChecklistTemplate(this.form.mst_corporate_id);
    },

    async removeChecklistFile(key) {
      const result = await Swal.fire({
        icon: "warning",
        title: "Hapus File?",
        text: "File checklist ini akan dihapus.",
        showCancelButton: true,
        confirmButtonText: "Ya, Hapus",
        cancelButtonText: "Batal",
      });
      if (!result.isConfirmed) return;

      if (!this.form.mst_corporate_id) {
        Swal.fire("Warning", "Pilih Customer terlebih dahulu", "warning");
        return;
      }

      try {
        await documentChecklistController.deleteFileByCorporate(
          this.form.mst_corporate_id,
          key,
        );
        this.clearChecklistFileLocal(key);
        await this.loadChecklistTemplate(this.form.mst_corporate_id);
      } catch (error) {
        console.error(error);
        Swal.fire(
          "Error",
          error.response?.data?.message || "Gagal menghapus file checklist",
          "error",
        );
      }
    },

    clearChecklistFileLocal(key) {
      if (!this.checklist.items?.length) return;

      this.checklist.items = this.checklist.items.map((i) =>
        i.key === key ? { ...i, file_path: null } : i,
      );

      const requiredMissing = this.checklist.items.filter(
        (i) => i.required && !i.file_path,
      );
      this.checklist.is_complete = requiredMissing.length === 0;
      this.checklist.missing = requiredMissing.map((i) => i.label);
    },

    getChecklistFileName(filePath) {
      if (!filePath) return "";
      return filePath.split("/").pop().split("\\").pop();
    },

    // ───────── FILE (upload baru) ─────────
    isAllowedOfferFile(file) {
      const name = (file.name || "").toLowerCase();
      const type = file.type || "";
      return (
        type === "application/pdf" ||
        type === "image/jpeg" ||
        type === "image/jpg" ||
        type === "image/png" ||
        /\.(pdf|jpe?g|png)$/.test(name)
      );
    },

    handleFileUpload(event) {
      const picked = Array.from(event.target.files || []);
      event.target.value = "";

      const valid = [];
      for (const f of picked) {
        if (!this.isAllowedOfferFile(f)) {
          Swal.fire(
            "Warning",
            `"${f.name}" ditolak. Hanya PDF, JPG, JPEG, atau PNG.`,
            "warning",
          );
          continue;
        }
        if (f.size > 10 * 1024 * 1024) {
          Swal.fire("Warning", `"${f.name}" melebihi 10MB.`, "warning");
          continue;
        }
        valid.push(f);
      }
      if (valid.length) {
        this.form.file = [...(this.form.file || []), ...valid];
      }
    },

    handleDrop(e) {
      this.isDragging = false;
      const dropped = Array.from(e.dataTransfer.files || []);
      const valid = [];
      for (const f of dropped) {
        if (!this.isAllowedOfferFile(f)) continue;
        if (f.size > 10 * 1024 * 1024) continue;
        valid.push(f);
      }
      if (!valid.length) {
        Swal.fire(
          "Warning",
          "Hanya file PDF / JPG / JPEG / PNG (maks 10MB) yang diterima.",
          "warning",
        );
        return;
      }
      this.form.file.push(...valid);
    },

    removeNewFile(index) {
      this.form.file.splice(index, 1);
    },

    getFileName(key) {
      if (!key) return "File Tidak Ditemukan";
      return key.split("/").pop().split("\\").pop();
    },

    async getSignedUrl(singleKey) {
      if (!singleKey) return "#";
      try {
        const res = await transportOffersController.getSignedUrl(singleKey);
        return res.url;
      } catch (err) {
        console.error(err);
        return "#";
      }
    },

    async downloadFile(singleKey) {
      if (!singleKey) return;
      try {
        const url = await this.getSignedUrl(singleKey);
        if (url && url !== "#") {
          window.open(url, "_blank");
        } else {
          Swal.fire("Error", "Gagal mendapatkan link download", "error");
        }
      } catch (err) {
        Swal.fire("Error", "Gagal mengunduh file", "error");
      }
    },

    // ───────── FILE (lama, dari tabel files) ─────────
    removeExistingFile(fileItem) {
      this.removedFileIds.push(fileItem.id);
      this.form.files = this.form.files.filter((f) => f.id !== fileItem.id);
    },

    async toggleFileStatus(fileItem, isDetailMode = false) {
      const newStatus = fileItem.status == 1 ? 0 : 1;
      const confirm = await Swal.fire({
        title: `${newStatus == 1 ? "Aktifkan" : "Nonaktifkan"} dokumen ini?`,
        text: this.getFileName(fileItem.file_key),
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#198754",
      });
      if (!confirm.isConfirmed) return;

      try {
        await transportOffersController.updateFileStatus(
          this.id,
          fileItem.id,
          newStatus,
        );
        fileItem.status = newStatus;
        if (isDetailMode) {
          Swal.fire({
            icon: "success",
            title: "Berhasil",
            timer: 1200,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "Gagal mengubah status dokumen", "error");
      }
    },

    // ───────── BUILD FORM DATA ─────────
    buildFormData() {
      const payload = new FormData();
      // Jangan append File / Object ke field arbitrary (picu Multer Unexpected field)
      const skipKeys = [
        "file",
        "files",
        "selectedEmployee",
        "selectedEmpolyee",
      ];

      Object.keys(this.form).forEach((key) => {
        if (skipKeys.includes(key)) return;
        const val = this.form[key];
        if (val === null || val === undefined) return;
        // skip File / Blob / Array non-primitif
        if (typeof File !== "undefined" && val instanceof File) return;
        if (typeof Blob !== "undefined" && val instanceof Blob) return;
        if (Array.isArray(val) || (typeof val === "object" && val !== null))
          return;

        if (key === "has_pks") {
          payload.append(key, val ? 1 : 0);
        } else if (key === "start_date" || key === "end_date") {
          payload.append(key, this.isFixedTerm ? val : "");
        } else {
          payload.append(key, val);
        }
      });

      (this.form.file || []).forEach((f) => {
        if (f) payload.append("new_files", f);
      });

      const keptKeys = (this.form.files || [])
        .map((f) => f.file_key)
        .filter(Boolean);
      payload.append("kept_keys", keptKeys.join("|"));

      return payload;
    },

    // ───────── SAVE ─────────
    async saveData() {
      if (!this.form.mst_corporate_id) {
        Swal.fire("Warning", "Customer wajib diisi", "warning");
        return;
      }
      if (!this.form.contract_type) {
        Swal.fire("Warning", "Contract type wajib diisi", "warning");
        return;
      }

      if (this.isFixedTerm) {
        if (!this.form.start_date || !this.form.end_date) {
          Swal.fire(
            "Warning",
            "Start Date dan End Date wajib diisi untuk contract type Fixed-Term",
            "warning",
          );
          return;
        }
        if (new Date(this.form.end_date) < new Date(this.form.start_date)) {
          Swal.fire(
            "Warning",
            "End Date tidak boleh lebih awal dari Start Date",
            "warning",
          );
          return;
        }
      }
      if (this.form.has_pks == 0 && !this.form.pks_reason?.trim()) {
        Swal.fire(
          "Warning",
          "Alasan tidak ada perjanjian kerja sama wajib diisi",
          "warning",
        );
        return;
      }

      this.isLoading = true;
      try {
        await this.uploadPendingChecklistFiles();

        const payload = this.buildFormData();

        if (this.mode === "add") {
          await transportOffersController.create(payload);
          await Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Penawaran Harga Biaya Angkut berhasil ditambahkan",
            timer: 2000,
            showConfirmButton: false,
          });
          this.$router.push("/transport/transport-offers");
        } else {
          await transportOffersController.update(this.id, payload);
          await Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Penawaran Harga Biaya Angkut berhasil diperbarui",
            timer: 2000,
            showConfirmButton: false,
          });
          await this.loadDetail();
          this.form.file = [];
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Gagal",
          text:
            error.response?.data?.message || "Terjadi kesalahan saat menyimpan",
        });
      } finally {
        this.isLoading = false;
      }
    },

    // ───────── HELPERS ─────────
    formatFileSize(bytes) {
      if (!bytes) return "";
      const kb = bytes / 1024;
      return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
    },

    getDaysText(days) {
      if (days === null || days === undefined || isNaN(Number(days))) return 0;
      return Math.abs(Number(days));
    },
  },

  async mounted() {
    await this.loadMasterData();
    if (this.isEditOrDetail) {
      await this.loadDetail();
    }
    this.isFormReady = true;
  },
};
